import SVG from 'react-inlinesvg'
import { useContextMotifGenerator } from '../../contexts/motifGeneratorContext'
import { useContextPanels } from '../../contexts/panelsContext'
import { useContextOnboarding } from '../../contexts/onboardingContext'
import { useNavigate } from 'react-router-dom'
import routePaths from '../../constants/routePaths'
import { useContextSelectedMotifs } from '../../contexts/selectedMotifContext'
import ConfirmationDialog from '../ConfirmationDialog'
import { useEffect, useRef, useState } from 'react'
import DotDotLoader from '../DotDotLoader'
import { UIElement } from '../../types/UIElements'
import { useEnv } from '../../utils/useEnv'
import { useContextScreensaver } from '../../contexts/screensaverContext'
import { useContextMotifDetails } from '../../contexts/motifDetailsContext'
import { useTranslation } from 'react-i18next'
import { FeatureDiscoveryElement } from '../../types/FeatureDiscoveryElement'
import { cn } from '../../utils/cn'
const ActionButtons = () => {
  const { setIsScreensaverOn } = useContextScreensaver()
  const {
    setExportInvoked,
    setExportModal,
    setLoading,
    loading,
    selectedGeneratorType,
    setFreeLayoutGeneratorState,
  } = useContextMotifGenerator()
  const { t } = useTranslation()
  const { isPanelOpen, closeAll } = useContextPanels()
  const {
    isFirstGeneratorVisit,
    featureDiscoveryElementsOpen,
    setFeatureDiscoveryElementsOpen,
    featureDiscoveryElementsAlreadyShown,
  } = useContextOnboarding()
  const navigate = useNavigate()
  const { setSelectedMotifs, setNumberOfSelectedMotifs } = useContextSelectedMotifs()
  const { motifMapViewport } = useContextMotifDetails()
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false)
  const [pulsing, setPulsing] = useState<boolean>(false)
  const { isKiosk } = useEnv()
  const featureDiscoveryRef = useRef<HTMLDivElement>(null)
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (featureDiscoveryElementsOpen.includes(FeatureDiscoveryElement.ACTIONS)) {
      featureDiscoveryRef.current?.classList.add('show')
    } else if (featureDiscoveryRef.current?.classList.contains('show')) {
      featureDiscoveryRef.current?.classList.add('hide')
    }
  }, [featureDiscoveryElementsOpen, featureDiscoveryElementsAlreadyShown])

  useEffect(() => {
    if (loading) {
      loadingTimeoutRef.current = setTimeout(() => {
        console.warn('Export timeout reached, resetting loading state')
        setLoading(false)
        setExportInvoked(false)
        setExportModal(false)
      }, 30000)
    } else {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
        loadingTimeoutRef.current = null
      }
    }

    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
      }
    }
  }, [loading])

  useEffect(() => {
    setPulsing(true)
    setTimeout(() => {
      setPulsing(false)
    }, 200)
  }, [selectedGeneratorType])

  const handleRefresh = (confirmed: boolean) => {
    if (confirmed) {
      motifMapViewport.current = undefined

      navigate(routePaths.HOME)
      setSelectedMotifs([])
      setNumberOfSelectedMotifs(0)
    }
    setShowConfirmation(false)
  }

  const handleDownload = () => {
    setFeatureDiscoveryElementsOpen([])
    setIsScreensaverOn(false)

    // Ha loading állapotban van, állítsd le
    if (loading) {
      setLoading(false)
      setExportInvoked(false)
      setExportModal(false)
      if (isKiosk) {
        closeAll()
      }
      return
    }

    if (isKiosk) {
      if (isPanelOpen(UIElement.QRMODAL)) {
        // If QR modal is already open, close it
        closeAll()
        setLoading(false)
      } else {
        // Open export modal to choose single/tiles
        setLoading(true)
        setExportInvoked(true)
        setExportModal(true)
      }
    } else {
      setLoading(true)
      setExportInvoked(true)
      setExportModal(true)
    }
  }

  return (
    <>
      <div className="fixed bottom-[4vh] right-[10px] flex h-[60px] items-center gap-[20px] text-[0.875rem] tall-xs:h-[70px] xs:tall-sm:bottom-[18vh] sm:bottom-[13vh] sm:h-[90px] sm:text-[1.125rem] md:right-[15px] md:h-[100px] lg:h-[120px] lg:text-[1.5rem] xl:right-[25px] xl:gap-[25px] xxl:bottom-[15vh] xxl:right-[calc(3vw_-_17px)] xxl:h-[136px] xxl:gap-[30px] [&_svg]:max-h-[50%] [&_svg]:max-w-[50%]">
        <div
          ref={featureDiscoveryRef}
          className="absolute right-[-10px] flex h-full select-none items-center whitespace-pre-wrap rounded-[60px_0_0_60px] bg-black pl-[30px] pr-[calc(2_*_40px_+_20px_+_10px)] text-white opacity-0 [transform:translateX(100%)] [transition:transform_0.4s] tall-xs:min-w-[330px] xs:min-w-[330px] xs:tall-sm:min-w-[350px] xs:tall-sm:pl-[40px] sm:min-w-[375px] sm:pr-[calc(2_*_70px_+_20px_+_20px_+_30px)] md:right-[-15px] md:min-w-[410px] md:pr-[calc(2_*_80px_+_20px_+_20px)] lg:min-w-[440px] xl:right-[-25px] xl:min-w-[485px] xl:pr-[calc(2_*_90px_+_20px_+_20px_+_30px)] xxl:right-[calc(-3vw_+_17px)] xxl:min-w-[calc(485px_+_3vw)] xxl:rounded-[80px_0_0_80px] xxl:pl-[44px] xxl:pr-[calc(2_*_112px_+_20px_+_20px_+_30px)] [&.hide]:animate-[fade-out-x50_0.6s_0s_forwards] [&.hide]:opacity-100 [&.hide]:[transform:translateX(0)] [&.show:not(.hide)]:animate-[fade-in-x50_0.6s_0.6s_forwards]"
        >
          {t('downloadOrRestart')}
        </div>
        <button
          className={cn(
            'relative z-10 m-auto flex h-[40px] w-[40px] items-center justify-center rounded-[50%] bg-black p-0 text-white shadow-floating [border:#000000] [transition:background-color_0.2s] tall-xxs:h-[50px] tall-xxs:w-[50px] sm:h-[70px] sm:w-[70px] md:h-[80px] md:w-[80px] xl:h-[90px] xl:w-[90px] xxl:h-[112px] xxl:w-[112px] [&.pulse]:animate-[pulse_0.2s] [&.red]:bg-red [&.red]:[transition-delay:0.6s] download',
            {
              pulse: pulsing,
              red:
                !featureDiscoveryElementsOpen.includes(FeatureDiscoveryElement.ACTIONS) &&
                !isFirstGeneratorVisit,
            },
          )}
          onClick={handleDownload}
          disabled={loading}
        >
          {loading ? (
            <DotDotLoader />
          ) : (
            <div
              className={cn(
                'group flex flex-col items-center justify-center text-[10px] [transition:0.25s_ease-out] md:text-[13px] xl:text-[16px] [&.tick]:[transform:rotate(360deg)]',
                { tick: isPanelOpen(UIElement.QRMODAL) },
              )}
            >
              <span className="h-[0.1875em] w-[1.3125em] bg-white [transition:0.2s_ease-out] group-[.tick]:w-0 group-[.tick]:opacity-0" />
              <span className="h-[1.75em] w-[0.1875em] bg-white [transition:0.2s_ease-out] group-[.tick]:h-0 group-[.tick]:opacity-0" />
              <span className="mt-[-1.0625em] size-[1em] bg-transparent [border-bottom:0.1875em_solid_#FFFFFF] [border-right:0.1875em_solid_#FFFFFF] [transform:rotate(45deg)] [transition:0.2s_ease-out] group-[.tick]:mt-[-0.8125em] group-[.tick]:h-[1.75em]" />
            </div>
          )}
        </button>
        <button
          className="relative z-10 m-auto flex h-[40px] w-[40px] items-center justify-center rounded-[50%] bg-black p-0 text-white shadow-floating [border:#000000] [transition:background-color_0.2s] tall-xxs:h-[50px] tall-xxs:w-[50px] sm:h-[70px] sm:w-[70px] md:h-[80px] md:w-[80px] xl:h-[90px] xl:w-[90px] xxl:h-[112px] xxl:w-[112px] [&.pulse]:animate-[pulse_0.2s] [&.red]:bg-red [&.red]:[transition-delay:0.6s]"
          onClick={() => {
            setIsScreensaverOn(false)
            setShowConfirmation(true)
            setFreeLayoutGeneratorState({ images: [], isMirrored: false })
          }}
        >
          <SVG className="reload-svg" src="/icons/reload_bold.svg" />
        </button>
      </div>
      {showConfirmation && (
        <ConfirmationDialog onConfirm={handleRefresh} message={'refreshConfirmation'} />
      )}
    </>
  )
}

export default ActionButtons
